import { jsx as _jsx } from "react/jsx-runtime";
import React, { Suspense, forwardRef } from 'react';
/**
 * Retry a dynamic import a few times before giving up — smooths over transient
 * network blips / a chunk requested mid-deploy. A genuine 404 (stale chunk after
 * a redeploy) still rejects after the retries and surfaces to the ErrorBoundary,
 * which offers a reload. Backoff is short and linear.
 */
function retryImport(load, retries = 3, delay = 400) {
    return () => new Promise((resolve, reject) => {
        const attempt = (n) => {
            load().then(resolve).catch((err) => {
                if (n <= 0)
                    return reject(err);
                setTimeout(() => attempt(n - 1), delay);
            });
        };
        attempt(retries);
    });
}
/**
 * Wraps a dynamically imported component so it can sit in the UXPComponents
 * barrel without pulling its module (and dependencies) into main.js.
 *
 * - The returned component exists synchronously, so app bundles that
 *   dereference window.UXPComponents.X at eval time keep working.
 * - Refs are forwarded (React.lazy passes them through to components that
 *   use forwardRef/useImperativeHandle, e.g. CodeEditor).
 * - Cast the result back to the original component type at the call site so
 *   the generated uxp.d.ts stays identical.
 */
export function lazyComponent(load, fallback = null) {
    const Lazy = React.lazy(retryImport(load));
    const Wrapped = forwardRef((props, ref) => (_jsx(Suspense, { fallback: fallback, children: _jsx(Lazy, Object.assign({}, props, { ref: ref })) })));
    Wrapped.displayName = 'UXPLazy';
    return Wrapped;
}
/**
 * Same idea for modules with named exports: returns a lazy component for one
 * named export of the module.
 */
export function lazyNamed(load, exportName, fallback = null) {
    return lazyComponent(() => load().then((m) => ({ default: m[exportName] })), fallback);
}
